/**
 * 商家端统一 IM 长连接客户端（xqyy_msg）
 *
 * 约定（见 AGENTS.md「商家端消息通知与 WebSocket 统一约定」）：
 * 1. 全端只接 xqyy_msg 一个 WS 服务，不新建第二条长连接（客服会话 / 审批通知 / 业务提醒共用）；
 * 2. 握手：wss://<im-host>/ws?tk=<token>&ty=UB&st=1B&bid=<businessId>
 *    - ty=UB 选 UserB；st=1B 命中商家端会话类型（客服席用 2B）；
 *    - 生产必须 wss，nginx / 服务端日志不得记录含 tk 的 query；
 * 3. 不新增 WS action code：系统通知由服务端以 code = 4（SEND_SINGLE_MSG）投递；
 * 4. 多标签页只保留一个连接（localStorage 选主 + BroadcastChannel 转发），
 *    避免同一 userId#UB 在 xqyy_msg 侧被后连接的页面顶掉导致消息丢失；
 * 5. 未读以服务端汇总为唯一口径：收到帧只触发「重新拉取未读」，
 *    不在前端做本地加减（见 src/stores/message.ts）。
 *
 * 心跳说明：xqyy_msg 侧对连接存活由其自身机制维护，本客户端不做业务心跳
 * （避免猜错 action code 反被服务端判为非法帧）；连接断开由 onclose/onerror 触发重连。
 */

/** 服务端投递型消息（与 xqyy_msg MsgActionEnum.SEND_SINGLE_MSG 对齐，不新增 action code） */
const CODE_SEND_SINGLE_MSG = 4;

/** 接收方用户类型：商家端（UserB） */
const USER_TYPE_BUSINESS = 'UB';

/** 会话类型：商家端后台（thdc_business_session_） */
const SYSTEM_TYPE_BUSINESS_MS = '1B';

/** 选主锁：localStorage 键 / 续租间隔 / 租约有效期 */
const LEADER_KEY = 'im_ws_leader';
const LEADER_TICK = 5000;
const LEADER_TTL = 15000;

/** 同源标签页消息通道（leader 把帧转发给 follower，保证多标签角标一致） */
const CHANNEL_NAME = 'xqyy_im_channel';

/** 重连退避：1s 起，最多 30s */
const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 30000;

export type ImFrameHandler = (frame: Message.IWsFrame) => void;
export type ImStateHandler = (connected: boolean) => void;

interface LeaderLock {
    id: string;
    ts: number;
}

/**
 * 唯一的商家端 IM 长连接实例（模块级单例）
 */
class ImWsClient {
    private readonly instanceId = `im_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    private socket: WebSocket | null = null;
    private channel: BroadcastChannel | null = null;

    private readonly frameHandlers = new Set<ImFrameHandler>();
    private readonly stateHandlers = new Set<ImStateHandler>();

    private started = false;
    private connected = false;

    private reconnectAttempt = 0;
    private reconnectTimer: number | null = null;
    private leaderTimer: number | null = null;
    private bindPageExit = false;

    /** 是否为主标签页（唯一建立 WS 连接的一方） */
    private leader = false;

    /**
     * 启动客户端（幂等）：注册标签页通道 + 选主，并作为 leader 建立连接。
     */
    start(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        if (typeof BroadcastChannel !== 'undefined') {
            this.channel = new BroadcastChannel(CHANNEL_NAME);
            this.channel.onmessage = (event: MessageEvent) => {
                const data = event.data;
                if (!data) {
                    return;
                }
                if (data.type === 'frame' && data.frame) {
                    this.dispatch(data.frame, false);
                } else if (data.type === 'state') {
                    this.setConnected(!!data.connected);
                }
            };
        }

        this.refreshLeader();
        this.leaderTimer = window.setInterval(() => this.refreshLeader(), LEADER_TICK);

        if (!this.bindPageExit) {
            this.bindPageExit = true;
            window.addEventListener('beforeunload', () => this.releaseLeader());
        }
    }

    /**
     * 停止客户端（退出登录 / 路由销毁时调用）。
     */
    stop(): void {
        this.started = false;
        this.closeSocket();
        if (this.leaderTimer !== null) {
            window.clearInterval(this.leaderTimer);
            this.leaderTimer = null;
        }
        this.releaseLeader();
        if (this.channel) {
            this.channel.close();
            this.channel = null;
        }
    }

    /** 订阅推送帧（返回取消订阅函数） */
    onFrame(handler: ImFrameHandler): () => void {
        this.frameHandlers.add(handler);
        return () => this.frameHandlers.delete(handler);
    }

    /** 订阅连接状态变化（返回取消订阅函数） */
    onStateChange(handler: ImStateHandler): () => void {
        this.stateHandlers.add(handler);
        return () => this.stateHandlers.delete(handler);
    }

    /** 当前是否已连接 */
    isConnected(): boolean {
        return this.connected;
    }

    /* ======================== 多标签页选主 ======================== */

    private readLock(): LeaderLock | null {
        try {
            const raw = window.localStorage.getItem(LEADER_KEY);
            if (!raw) {
                return null;
            }
            const lock = JSON.parse(raw) as LeaderLock;
            return lock && lock.id && lock.ts ? lock : null;
        } catch (e) {
            return null;
        }
    }

    private writeLock(): void {
        const lock: LeaderLock = { id: this.instanceId, ts: Date.now() };
        window.localStorage.setItem(LEADER_KEY, JSON.stringify(lock));
    }

    private releaseLeader(): void {
        if (!this.leader) {
            return;
        }
        const lock = this.readLock();
        if (lock && lock.id === this.instanceId) {
            window.localStorage.removeItem(LEADER_KEY);
        }
        this.leader = false;
    }

    /**
     * 选主续租：租约过期时当前标签页接管并建连；已是 leader 则续租。
     */
    private refreshLeader(): void {
        if (!this.started) {
            return;
        }
        const lock = this.readLock();
        const expired = !lock || Date.now() - lock.ts > LEADER_TTL;

        if (this.leader) {
            if (lock && lock.id !== this.instanceId && !expired) {
                // 被其它标签页接管（例如本页刚从后台恢复）：让位并断开，由新 leader 承载
                this.leader = false;
                this.closeSocket();
                return;
            }
            this.writeLock();
            return;
        }

        if (expired) {
            this.leader = true;
            this.writeLock();
            this.connect();
        }
    }

    /* ======================== 连接与重连 ======================== */

    private connect(): void {
        const url = this.resolveUrl();
        if (!url) {
            // 未配置 VITE_IM_BASE_URL 或未登录：不建立长连接，仅由 HTTP 拉取未读
            return;
        }
        if (
            this.socket &&
            (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }
        try {
            const socket = new WebSocket(url);
            this.socket = socket;
            socket.onopen = () => {
                this.reconnectAttempt = 0;
                this.setConnected(true);
            };
            socket.onmessage = (event: MessageEvent) => this.handleRawMessage(event);
            socket.onclose = () => {
                this.setConnected(false);
                this.socket = null;
                this.scheduleReconnect();
            };
            socket.onerror = () => {
                this.setConnected(false);
            };
        } catch (e) {
            this.scheduleReconnect();
        }
    }

    private closeSocket(): void {
        if (this.reconnectTimer !== null) {
            window.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        const socket = this.socket;
        this.socket = null;
        this.setConnected(false);
        if (socket) {
            socket.onopen = null;
            socket.onmessage = null;
            socket.onclose = null;
            socket.onerror = null;
            if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                socket.close();
            }
        }
    }

    private scheduleReconnect(): void {
        if (!this.started || !this.leader || this.reconnectTimer !== null) {
            return;
        }
        const delay = Math.min(RECONNECT_BASE * Math.pow(2, this.reconnectAttempt), RECONNECT_MAX);
        this.reconnectAttempt += 1;
        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }

    /* ======================== 帧处理 ======================== */

    private handleRawMessage(event: MessageEvent): void {
        let frame: Message.IWsFrame | null = null;
        try {
            frame = typeof event.data === 'string' ? JSON.parse(event.data) : (event.data as Message.IWsFrame);
        } catch (e) {
            return;
        }
        if (!frame || !frame.code) {
            return;
        }
        // 仅处理投递帧（服务端 → 客户端的私聊/系统通知，code = 4），其余帧不处理也不新增 action code
        if (frame.code !== CODE_SEND_SINGLE_MSG) {
            return;
        }
        this.dispatch(frame, true);
    }

    /** 分发帧：本地处理器优先，并转发给同源其它标签页（保证多标签角标一致） */
    private dispatch(frame: Message.IWsFrame, forward: boolean): void {
        this.frameHandlers.forEach((handler) => {
            try {
                handler(frame);
            } catch (e) {
                // 单个订阅者异常不影响其它订阅者
            }
        });
        if (forward && this.channel) {
            this.channel.postMessage({ type: 'frame', frame });
        }
    }

    private setConnected(connected: boolean): void {
        if (this.connected === connected) {
            return;
        }
        this.connected = connected;
        this.stateHandlers.forEach((handler) => {
            try {
                handler(connected);
            } catch (e) {
                // 忽略订阅者异常
            }
        });
        if (this.channel) {
            this.channel.postMessage({ type: 'state', connected });
        }
    }

    /* ======================== 握手地址 ======================== */

    /**
     * 组装握手地址：wss://<im-host>/ws?tk=<token>&ty=UB&st=1B&bid=<businessId>
     *
     * 返回 null 表示当前不满足建连条件（未配置地址 / 未登录），调用方直接跳过。
     */
    private resolveUrl(): string | null {
        const base = (import.meta.env.VITE_IM_BASE_URL || '').trim();
        if (!base) {
            return null;
        }
        const token = window.localStorage.getItem('token') || '';
        if (!token) {
            return null;
        }
        const separator = base.indexOf('?') >= 0 ? '&' : '?';
        const params = [
            `tk=${encodeURIComponent(token)}`,
            `ty=${USER_TYPE_BUSINESS}`,
            `st=${SYSTEM_TYPE_BUSINESS_MS}`
        ];
        const businessId = this.resolveBusinessId();
        if (businessId) {
            params.push(`bid=${encodeURIComponent(businessId)}`);
        }
        return `${base}${separator}${params.join('&')}`;
    }

    /**
     * 商家 id：登录信息优先，回退当前选中店铺；取不到时不带 bid 参数
     * （服务端按 B 端 userId 全局唯一做租户隔离，bid 仅作辅助）。
     */
    private resolveBusinessId(): string {
        try {
            const raw = window.localStorage.getItem('userInfo');
            if (raw) {
                const info = JSON.parse(raw) || {};
                const bid = info.businessId ?? info.business_id;
                if (bid) {
                    return String(bid);
                }
            }
        } catch (e) {
            // 解析失败走回退
        }
        return window.localStorage.getItem('currentShopIds') || '';
    }
}

export const imWsClient = new ImWsClient();